import { useState } from 'react';
import { Loader2, Check } from 'lucide-react';
import { useTodayChores, useCompleteChore, useUndoChoreCompletion } from '../../api/hooks';
import { analytics } from '../../lib/analytics';
import { ChoreCompletionModal } from '../ChoreCompletionModal';

/**
 * Displays today's chores for all family members in the sidebar
 *
 * Features:
 * - Shows chores as single items with stacked member dots
 * - Single-member chores: click to complete directly
 * - Multi-member chores: click opens popover to select who completed
 * - Partial completion indicator for shared chores
 * - Empty/loading states
 *
 * Used by:
 * - Sidebar.tsx (expanded sidebar view)
 *
 * Design rationale:
 * - Shared chores consolidated into single items (vs flattened per-member)
 * - Popover selection for shared chores reduces visual clutter
 * - Single-member chores maintain fast direct completion
 */
export function SidebarChoreList() {
  const { data: todayChores, isLoading } = useTodayChores();
  const completeChore = useCompleteChore();
  const undoCompletion = useUndoChoreCompletion();
  const [activeChore, setActiveChore] = useState<NonNullable<typeof todayChores>[number] | null>(null);

  /**
   * Handle chore click - either complete directly or show modal
   * - Single incomplete member: complete directly
   * - Multiple members: show modal for selection
   */
  const handleChoreClick = (chore: NonNullable<typeof todayChores>[number]) => {
    const incompleteMembers = chore.assignedMembers.filter((m) => !m.completed);

    if (incompleteMembers.length === 1 && incompleteMembers[0]) {
      // Single incomplete member - complete directly without modal
      const member = incompleteMembers[0];
      completeChore.mutate({
        choreId: chore.id,
        memberId: member.id,
      });
      analytics.sidebarChoreCompleted(chore.id, member.id, chore.points);
    } else {
      // Multiple members or all complete (for undo) - show modal
      setActiveChore(chore);
    }
  };

  /**
   * Handle member selection from modal
   */
  const handleMemberSelect = (memberId: string, isCompleted: boolean) => {
    if (!activeChore) return;

    if (isCompleted) {
      undoCompletion.mutate({ choreId: activeChore.id, memberId });
    } else {
      completeChore.mutate({ choreId: activeChore.id, memberId });
      analytics.sidebarChoreCompleted(activeChore.id, memberId, activeChore.points);
    }
  };

  if (isLoading) {
    return (
      <div className="sidebar-chore-section">
        <div className="sidebar-chore-header">Today's Chores</div>
        <div className="sidebar-chore-list flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-text-muted" />
        </div>
      </div>
    );
  }

  // Sort chores: incomplete first (by completion ratio), then by title
  const sortedChores = [...(todayChores || [])].sort((a, b) => {
    const aCompleteRatio =
      a.assignedMembers.filter((m) => m.completed).length / a.assignedMembers.length;
    const bCompleteRatio =
      b.assignedMembers.filter((m) => m.completed).length / b.assignedMembers.length;

    if (aCompleteRatio !== bCompleteRatio) {
      return aCompleteRatio - bCompleteRatio; // Less complete first
    }
    return a.title.localeCompare(b.title);
  });

  return (
    <div className="sidebar-chore-section">
      <div className="sidebar-chore-header lg:text-sm lg:font-bold lg:px-6">Today's Chores</div>
      <div className="sidebar-chore-list">
        {sortedChores.length === 0 ? (
          <div className="sidebar-chore-empty">No chores for today</div>
        ) : (
          sortedChores.map((chore) => {
            const completedCount = chore.assignedMembers.filter((m) => m.completed).length;
            const totalMembers = chore.assignedMembers.length;
            // For daily chores, allCompleted means someone completed it (not all members)
            // For other recurrence types, allCompleted means all members completed
            const allCompleted =
              chore.recurrence === 'daily'
                ? chore.completedByMember !== undefined && chore.completedByMember !== null
                : completedCount === totalMembers;
            const partiallyCompleted = completedCount > 0 && !allCompleted;

            return (
              <div
                key={chore.id}
                className="sidebar-chore-item relative cursor-pointer lg:py-4"
                onClick={() => handleChoreClick(chore)}
              >
                {/* Completion checkbox */}
                <div
                  className={`w-5 h-5 lg:w-8 lg:h-8 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
                    allCompleted
                      ? 'bg-sage border-sage'
                      : partiallyCompleted
                        ? 'sidebar-chore-checkbox--partial'
                        : 'border-border-default hover:border-teal'
                  }`}
                >
                  {allCompleted && <Check className="w-3 h-3 lg:w-5 lg:h-5 text-white" />}
                  {partiallyCompleted && (
                    <div className="w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full bg-text-primary" />
                  )}
                </div>

                {/* Member dots - stacked for multiple members, sorted alphabetically */}
                <div className="member-dots-stack">
                  {[...chore.assignedMembers].sort((a, b) => a.name.localeCompare(b.name)).slice(0, 3).map((member) => (
                    <div
                      key={member.id}
                      className={`member-dot ${member.completed ? 'member-dot--completed opacity-50' : ''}`}
                      style={{ backgroundColor: member.color }}
                      title={`${member.name}${member.completed ? ' (done)' : ''}`}
                    />
                  ))}
                  {chore.assignedMembers.length > 3 && (
                    <span className="text-xs text-text-muted ml-1">
                      +{chore.assignedMembers.length - 3}
                    </span>
                  )}
                </div>

                {/* Chore title */}
                <span
                  className={`sidebar-chore-title lg:text-base ${
                    allCompleted ? 'sidebar-chore-title--completed' : ''
                  }`}
                >
                  {chore.title}
                </span>

                {/* Points */}
                <span className="sidebar-chore-points">{chore.points}</span>
              </div>
            );
          })
        )}
      </div>

      {/* Chore completion modal */}
      {activeChore && (
        <ChoreCompletionModal
          choreId={activeChore.id}
          choreTitle={activeChore.title}
          chorePoints={activeChore.points}
          choreRecurrence={activeChore.recurrence}
          completedByMember={activeChore.completedByMember}
          members={activeChore.assignedMembers}
          onSelectMember={handleMemberSelect}
          onClose={() => setActiveChore(null)}
          source="sidebar"
          isPending={completeChore.isPending || undoCompletion.isPending}
        />
      )}
    </div>
  );
}
